import * as Yup from 'yup';
//utils
import {expirationDateSchema, logoOrImageSchema} from '@/utils/validation';
//types
import {IOrganizationTableInfo} from '@/utils/types';
//helpers
import {CREATE_EVENT_STEP} from './helpers';

export type EventSponsor = {
  logo: File | string;
  id: string | number;
  categoryId: number;
  name: string;
  sortOrder: number;
  category?: {
    id?: number;
    name: string;
    sortOrder: number;
  };
};

export type CreateEventFormValues = {
  id?: number;
  organizationId: number | null;
  name: string;
  goalAmount: number | null;
  isGoalAmount: boolean;
  description: string;
  location: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  ticketsSalesStartDate: string;
  ticketsSalesStartTime: string;
  ticketsSalesEndDate: string;
  ticketsSalesEndTime: string;
  sponsors: EventSponsor[];
  tableInfos: IOrganizationTableInfo[];
  customizeWebpage: string;
  primaryColor: string;
  isOnlineAuction: boolean;
  startAuctionDate: string;
  startAuctionTime: string;
  endAuctionDate: string;
  endAuctionTime: string;
  startRaffleDate: string;
  startRaffleTime: string;
  endRaffleDate: string;
  endRaffleTime: string;
  isFundTheMission: boolean;
  isRaffle: boolean;
  raffleDescription: string;
  quantity: number | null;
  logo: string | File;
  prizeLogo: string | File;
  webpageLogo?: string | File;
  raffleTires: {numberOfTickets: number | null; price: number; id?: number}[];
};

export const initialValues: CreateEventFormValues = {
  organizationId: null,
  name: '',
  goalAmount: null,
  isGoalAmount: false,
  description: '',
  location: '',
  startDate: '',
  startTime: '12:00 am',
  endDate: '',
  endTime: '12:00 am',
  ticketsSalesStartDate: '',
  ticketsSalesStartTime: '12:00 am',
  ticketsSalesEndDate: '',
  ticketsSalesEndTime: '12:00 am',
  sponsors: [],
  tableInfos: [],
  customizeWebpage: '',
  primaryColor: '',
  isOnlineAuction: false,
  startAuctionDate: '',
  startAuctionTime: '12:00 am',
  endAuctionDate: '',
  endAuctionTime: '12:00 am',
  startRaffleDate: '',
  startRaffleTime: '12:00 am',
  endRaffleDate: '',
  endRaffleTime: '12:00 am',
  isFundTheMission: false,
  isRaffle: false,
  raffleDescription: '',
  quantity: null,
  logo: '',
  prizeLogo: '',
  raffleTires: [],
};

const tableInfoSchema = Yup.object().shape({
  tier: Yup.string().required('Tickers tier is required'),
  numberOfSeats: Yup.number().required('Seats is required').positive('Number must be positive'),
  amount: Yup.number().required('Amount is required').positive('Amount must be positive'),
  price: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return parseFloat(originalValue.replace(',', '.'));
      }
      return value;
    })
    .required('Price is a required')
    .typeError('Price must be a number')
    .positive('Price must be a positive')
    .min(0.01, 'Min. price is 0.01')
    .test(
      'maxDigitsAfterDecimal',
      'Max two decimal places',
      (number) => number === undefined || number === null || Number.isInteger(number * 100)
    ),
  fairMarketValue: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return parseFloat(originalValue.replace(',', '.'));
      }
      return value;
    })
    .typeError('Price must be a number')
    .positive('Price must be a positive')
    .min(0.01, 'Min. price is 0.01')
    .test(
      'maxDigitsAfterDecimal',
      'Max two decimal places',
      (number) => number === undefined || number === null || Number.isInteger(number * 100)
    ),
});

const raffleTireSchema = Yup.object().shape({
  numberOfTickets: Yup.number()
    .typeError('Must be a number')
    .integer('Tickets must be an integer')
    .positive('Tickets must be positive')
    .when('$isRaffle', {
      is: true,
      then: (schema) => schema.required('Number of tickets is required'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  price: Yup.number()
    .typeError('Must be a number')
    .when('$isRaffle', {
      is: true,
      then: (schema) => schema.required('Price is required').positive('Price must be positive'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
});

export const validationSchema = (activeStep: number) => {
  return Yup.object().shape({
    ...(activeStep === CREATE_EVENT_STEP.SETTING && {
      name: Yup.string().required('Event name is required').trim(),
      logo: logoOrImageSchema,
      location: Yup.string().required('Event location is required').trim(),
      startDate: expirationDateSchema.required('Date is required'),
      startTime: Yup.string().required('Time is required').trim(),
      endDate: expirationDateSchema.required('Date is required'),
      endTime: Yup.string().required('Time is required').trim(),
      isGoalAmount: Yup.boolean(),
      goalAmount: Yup.number().when('isGoalAmount', {
        is: true,
        then: (schema) => schema.required('Amount is required').positive('Amount must be positive'),
        otherwise: (schema) => schema.notRequired(),
      }),
      customizeWebpage: Yup.string()
        .required('URL is required')
        .matches(/^[a-z0-9-]+$/, 'Invalid characters (use a-z, 0-9, -)')
        .test(
          'no-hyphens-at-edges',
          'Cannot start or end with a hyphen',
          (val) => !val?.startsWith('-') && !val?.endsWith('-')
        )
        .test('not-reserved', 'Reserved URL', (val) => val !== 'organization'),
      primaryColor: Yup.string().trim(),
    }),

    ...(activeStep === CREATE_EVENT_STEP.TABLES_AND_TICKETS && {
      ticketsSalesStartDate: expirationDateSchema.required('Date is required'),
      ticketsSalesStartTime: Yup.string().required('Time is required').trim(),
      ticketsSalesEndDate: expirationDateSchema.required('Date is required'),
      ticketsSalesEndTime: Yup.string().required('Time is required').trim(),
      tableInfos: Yup.array().of(tableInfoSchema),
    }),

    ...(activeStep === CREATE_EVENT_STEP.WEBPAGE && {
      isOnlineAuction: Yup.boolean(),
      startAuctionDate: Yup.string().when('isOnlineAuction', {
        is: true,
        then: () => expirationDateSchema.required('Date is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      startAuctionTime: Yup.string().test({
        name: 'startAuctionTime',
        test(value) {
          return !this.parent.isOnlineAuction || (value !== undefined && value !== '');
        },
        message: 'Time is required',
      }),
      endAuctionDate: Yup.string().when('isOnlineAuction', {
        is: true,
        then: () => expirationDateSchema.required('Date is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      endAuctionTime: Yup.string().test({
        name: 'endAuctionTime',
        test(value) {
          return !this.parent.isOnlineAuction || (value !== undefined && value !== '');
        },
        message: 'Time is required',
      }),

      startRaffleDate: Yup.string().when('isRaffle', {
        is: true,
        then: () => expirationDateSchema.required('Date is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      startRaffleTime: Yup.string().test({
        name: 'startRaffleTime',
        test(value) {
          return !this.parent.isRaffle || (value !== undefined && value !== '');
        },
        message: 'Time is required',
      }),
      endRaffleDate: Yup.string().when('isRaffle', {
        is: true,
        then: () => expirationDateSchema.required('Date is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      endRaffleTime: Yup.string().test({
        name: 'endRaffleTime',
        test(value) {
          return !this.parent.isRaffle || (value !== undefined && value !== '');
        },
        message: 'Time is required',
      }),
      isFundTheMission: Yup.boolean(),
      isRaffle: Yup.boolean(),
      raffleDescription: Yup.string()
        .trim()
        .when('isRaffle', {
          is: true,
          then: (schema) => schema.required('Description is required'),
          otherwise: (schema) => schema.notRequired(),
        }),

      quantity: Yup.number().positive('Quantity must be positive').nullable(),
      raffleTires: Yup.array().of(raffleTireSchema),
    }),
  });
};
